import type { ApiError } from '@/types/api.types';

function formatRetryDuration(seconds: number): string {
  const safe = Math.max(1, Math.ceil(seconds));
  if (safe >= 3600) {
    const hours = Math.ceil(safe / 3600);
    return `${hours} hour${hours === 1 ? '' : 's'}`;
  }
  if (safe >= 60) {
    const minutes = Math.ceil(safe / 60);
    return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  }
  return `${safe} second${safe === 1 ? '' : 's'}`;
}

function retryHint(retryAfterSeconds?: number): string {
  if (!retryAfterSeconds) return '';
  return ` Try again in ${formatRetryDuration(retryAfterSeconds)}.`;
}

/** Maps authentication API failures to accessible, status-specific guidance. */
export function formatAuthErrorMessage(error: ApiError, context: 'login' | 'register'): string {
  const retry = retryHint(error.retryAfterSeconds);

  switch (error.status) {
    case 401:
      return context === 'login'
        ? 'Incorrect email or password. Check your details and try again.'
        : (error.message ?? 'We could not create your account. Check your details and try again.');
    case 409:
      return error.message ?? 'An account with this email already exists.';
    case 423:
      return `This account is temporarily locked.${retry || ' Try again later.'}`;
    case 429:
      return `Too many attempts.${retry || ' Please wait and try again.'}`;
    default:
      return (
        error.message ??
        (context === 'login'
          ? 'Unable to sign in. Please try again.'
          : 'Account creation failed. Please try again.')
      );
  }
}
